import React, { useState, useEffect } from 'react';
import { useTranslation } from '../../hooks/useTranslation';
import { API_CONFIG } from '../../config';
import { ICONS } from '../../constants';

interface SpecializationsPanelProps {
  canEdit: boolean;
}

interface Specialization {
  name: string;
  order: number;
  hidden: boolean;
  usageCount: number;
}

const SpecializationsPanel: React.FC<SpecializationsPanelProps> = ({ canEdit }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [specializations, setSpecializations] = useState<Specialization[]>([]);
  const [newSpecName, setNewSpecName] = useState('');
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    fetchSpecializations();
  }, []);

  const fetchSpecializations = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('spellbook_token');
      const response = await fetch(`${API_CONFIG.API_URL}/rates/specializations`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch specializations');
      }

      const data = await response.json();
      setSpecializations(data.specializations);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error loading specializations');
    } finally {
      setLoading(false);
    }
  };

  const handleMoveUp = (index: number) => {
    if (index === 0) return;
    const newSpecs = [...specializations];
    [newSpecs[index - 1], newSpecs[index]] = [newSpecs[index], newSpecs[index - 1]];
    // Update order values
    newSpecs.forEach((spec, i) => { spec.order = i; });
    setSpecializations(newSpecs);
    setHasChanges(true);
    setSuccess(null);
  };

  const handleMoveDown = (index: number) => {
    if (index === specializations.length - 1) return;
    const newSpecs = [...specializations];
    [newSpecs[index], newSpecs[index + 1]] = [newSpecs[index + 1], newSpecs[index]];
    // Update order values
    newSpecs.forEach((spec, i) => { spec.order = i; });
    setSpecializations(newSpecs);
    setHasChanges(true);
    setSuccess(null);
  };

  const handleToggleHidden = (index: number) => {
    const newSpecs = [...specializations];
    newSpecs[index].hidden = !newSpecs[index].hidden;
    setSpecializations(newSpecs);
    setHasChanges(true);
    setSuccess(null);
  };

  const handleAddSpec = async () => {
    if (!newSpecName.trim()) return;

    try {
      setSaving(true);
      setError(null);

      const token = localStorage.getItem('spellbook_token');
      const response = await fetch(`${API_CONFIG.API_URL}/rates/specializations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ name: newSpecName.trim() }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to add specialization');
      }

      setNewSpecName('');
      await fetchSpecializations();
      setSuccess(t('rates.specializations.saveSuccess') || 'Специализация добавлена');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error adding specialization');
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);

      const order = specializations.map(s => s.name);
      const hidden = specializations.filter(s => s.hidden).map(s => s.name);

      const token = localStorage.getItem('spellbook_token');
      const response = await fetch(`${API_CONFIG.API_URL}/rates/specializations`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ order, hidden }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to save specializations');
      }

      setSuccess(t('rates.specializations.saveSuccess') || 'Специализации сохранены');
      setHasChanges(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error saving specializations');
    } finally {
      setSaving(false);
    }
  };

  const activeSpecs = specializations.filter(s => !s.hidden);
  const hiddenSpecs = specializations.filter(s => s.hidden);

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500"></div>
        <span className="ml-3 text-gray-400">{t('rates.common.loading') || 'Загрузка...'}</span>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <h3 className="text-xl font-semibold text-white mb-2">
          {t('rates.specializations.title') || 'Специализации'}
        </h3>
        <p className="text-gray-400 text-sm">
          {t('rates.specializations.description') || 'Управление списком доступных специализаций'}
        </p>
      </div>

      {/* Error/Success messages */}
      {error && (
        <div className="mb-4 p-3 bg-red-500/20 border border-red-500/50 rounded-lg text-red-400 text-sm">
          {error}
        </div>
      )}
      {success && (
        <div className="mb-4 p-3 bg-green-500/20 border border-green-500/50 rounded-lg text-green-400 text-sm">
          {success}
        </div>
      )}

      {/* Not editable warning */}
      {!canEdit && (
        <div className="mb-4 p-3 bg-yellow-500/20 border border-yellow-500/50 rounded-lg text-yellow-400 text-sm">
          {t('rates.common.noPermission') || 'Недостаточно прав для редактирования'}
        </div>
      )}

      {/* Add new specialization */}
      {canEdit && (
        <div className="mb-6 flex space-x-3">
          <input
            type="text"
            value={newSpecName}
            onChange={(e) => setNewSpecName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleAddSpec()}
            placeholder={t('rates.specializations.placeholder') || 'Название специализации'}
            className="flex-1 px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white
              placeholder-gray-400 focus:outline-none focus:border-indigo-500"
          />
          <button
            onClick={handleAddSpec}
            disabled={saving || !newSpecName.trim()}
            className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg
              transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
          >
            <ICONS.Plus className="w-4 h-4" />
            <span>{t('rates.specializations.addNew') || 'Добавить'}</span>
          </button>
        </div>
      )}

      {/* Active specializations */}
      <div className="mb-6">
        <h4 className="text-sm font-medium text-gray-400 mb-3 uppercase tracking-wide">
          {t('rates.specializations.active') || 'Активные'} ({activeSpecs.length})
        </h4>
        <div className="space-y-2">
          {activeSpecs.map((spec, index) => {
            const globalIndex = specializations.findIndex(s => s.name === spec.name);
            return (
              <div
                key={spec.name}
                className="flex items-center justify-between p-3 bg-gray-700/30 rounded-lg group"
              >
                <div className="flex items-center space-x-3">
                  <span className="text-white font-medium">{spec.name}</span>
                  {spec.usageCount > 0 && (
                    <span className="text-xs text-gray-500">
                      ({spec.usageCount} {t('common.uses') || 'использований'})
                    </span>
                  )}
                </div>
                
                {canEdit && (
                  <div className="flex items-center space-x-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={() => handleMoveUp(globalIndex)}
                      disabled={globalIndex === 0}
                      className="p-1.5 text-gray-400 hover:text-white hover:bg-gray-600 rounded
                        disabled:opacity-30 disabled:cursor-not-allowed"
                      title={t('rates.specializations.moveUp') || 'Вверх'}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                      </svg>
                    </button>
                    <button
                      onClick={() => handleMoveDown(globalIndex)}
                      disabled={globalIndex === specializations.length - 1}
                      className="p-1.5 text-gray-400 hover:text-white hover:bg-gray-600 rounded
                        disabled:opacity-30 disabled:cursor-not-allowed"
                      title={t('rates.specializations.moveDown') || 'Вниз'}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    <button
                      onClick={() => handleToggleHidden(globalIndex)}
                      className="p-1.5 text-gray-400 hover:text-yellow-400 hover:bg-gray-600 rounded"
                      title={t('rates.specializations.hide') || 'Скрыть'}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                      </svg>
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Hidden specializations */}
      {hiddenSpecs.length > 0 && (
        <div className="mb-6">
          <h4 className="text-sm font-medium text-gray-500 mb-3 uppercase tracking-wide">
            {t('rates.specializations.hidden') || 'Скрытые'} ({hiddenSpecs.length})
          </h4>
          <div className="space-y-2">
            {hiddenSpecs.map((spec) => {
              const globalIndex = specializations.findIndex(s => s.name === spec.name);
              return (
                <div
                  key={spec.name}
                  className="flex items-center justify-between p-3 bg-gray-800/50 rounded-lg opacity-60 group"
                >
                  <span className="text-gray-400">{spec.name}</span>
                  
                  {canEdit && (
                    <button
                      onClick={() => handleToggleHidden(globalIndex)}
                      className="p-1.5 text-gray-500 hover:text-green-400 hover:bg-gray-700 rounded
                        opacity-0 group-hover:opacity-100 transition-opacity"
                      title={t('rates.specializations.show') || 'Показать'}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Save button */}
      {canEdit && hasChanges && (
        <div className="mt-6 flex justify-end">
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center space-x-2 px-6 py-2.5 rounded-lg font-medium transition-all
              bg-indigo-600 hover:bg-indigo-700 text-white disabled:opacity-50"
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span>{t('rates.common.loading') || 'Сохранение...'}</span>
              </>
            ) : (
              <>
                <ICONS.Edit className="w-4 h-4" />
                <span>{t('rates.common.save') || 'Сохранить'}</span>
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
};

export default SpecializationsPanel;
